import * as React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import DataCatalogSyncTable from '../DataCatalogSyncTable';
import { translateValue } from '../utils';
import AccurityIconButton from '../../../buttons/components/AccurityIconButton';
import AccurityButton from '../../../buttons/components/AccurityButton';
import ChangeCell from './DataStructureSyncSelectionDialogChangeCell';
import Footer from './DataStructureSyncSelectionDialogFooter';
import { DATA_STRUCTURE_TYPE, NOT_NULL, STATUS, } from '../../types';
import { DialogTitle } from './DialogTitle';
import { isCheckboxDisabled } from './dataStructureDialogUtils';
import { useTableSelection } from './useTableSelection';
var CHILDREN_ATTRIBUTE = 'dataFields';
var COLUMNS = ['name', 'status', 'type'];
var INNER_COLUMNS = ['Name', 'Status', 'Not Null', 'Data Type'];
var FILTER_CONFIGS = {
    status: {
        type: 'select',
        display: ['Accurity', 'Source', 'No Updates', 'Update Available'],
        values: STATUS,
        initiallyEnabled: [true, true, true, true]
    },
    type: {
        type: 'select',
        display: ['Table', 'View', 'Collection', 'Sheet'],
        values: DATA_STRUCTURE_TYPE,
    },
};
var NOT_NULL_TRANSLATOR = {
    display: ['Yes', 'No'],
    values: NOT_NULL,
};
function renderCellContent(column, row) {
    var fontProps = {
        color: isCheckboxDisabled(row) ? 'text.disabled' : 'inherit',
        lineHeight: 'inherit',
        fontSize: 'inherit'
    };
    switch (column) {
        case 'name':
            return (React.createElement(Typography, { sx: fontProps }, row[column]));
        case 'status':
            return (React.createElement(Typography, { sx: fontProps }, translateValue(row[column], FILTER_CONFIGS.status)));
        case 'type':
            return (React.createElement(Typography, { sx: fontProps }, translateValue(row[column], FILTER_CONFIGS.type)));
        default:
            throw new Error("unable to render column: " + column);
    }
}
function renderInnerCellContent(column, row) {
    switch (column) {
        case 'Data Type':
            return React.createElement(ChangeCell, { change: row.dataType });
        case 'Not Null':
            return React.createElement(ChangeCell, { change: row.notNull, translator: NOT_NULL_TRANSLATOR });
        case 'Status':
            return translateValue(row.status, FILTER_CONFIGS.status);
        case 'Name':
            return row.name;
        default:
            throw new Error("unable to render column: " + column);
    }
}
export default function DataStructureSyncSelectionDialog(_a) {
    var _b = _a.triggeredFrom, triggeredFrom = _b === void 0 ? [] : _b, data = _a.data, existingObjects = _a.existingObjects, maximumObjects = _a.maximumObjects, handleClose = _a.handleClose;
    if (!data.length) {
        throw new Error('One or more data rows must be given');
    }
    // single line -> display only inner table
    var isSingleRow = data.length === 1;
    // all items are sources -> several visual changes
    var isAllSource = React.useMemo(function () { return data.every(function (row) { return row.status === 'SOURCE'; }); }, [data]);
    // status column is not visible with isAllSource
    var columns = React.useMemo(function () { return isAllSource ? COLUMNS.filter(function (col) { return col !== 'status'; }) : COLUMNS; }, [isAllSource]);
    var innerColumns = React.useMemo(function () { return isAllSource ? INNER_COLUMNS.filter(function (col) { return col !== 'Status'; }) : INNER_COLUMNS; }, [isAllSource]);
    var _c = useTableSelection(data), right = _c.right, left = _c.left, leftChecked = _c.leftChecked, setLeftChecked = _c.setLeftChecked, rightChecked = _c.rightChecked, setRightChecked = _c.setRightChecked, moveRight = _c.moveRight, moveLeft = _c.moveLeft;
    var sourceObjectsLength = right.filter(function (row) { return row.status === 'SOURCE'; }).length;
    var isMaximumObjectsReached = maximumObjects !== undefined && ((sourceObjectsLength + existingObjects) > maximumObjects);
    return (React.createElement(Dialog, { className: "dataCatalogSyncDialog dataCatalogSyncSelectionDialog", fullWidth: true, open: true },
        React.createElement(DialogTitle, { isAllSource: isAllSource, triggeredFrom: triggeredFrom, dataStructuresCount: data.length }),
        isSingleRow && (React.createElement(Stack, { direction: "row", spacing: 1, height: "100%", overflow: "hidden", padding: "20px 32px" },
            React.createElement(DataCatalogSyncTable, { data: right, columns: columns, innerColumns: innerColumns, filterConfigs: FILTER_CONFIGS, childrenAttribute: CHILDREN_ATTRIBUTE, noDataNotification: "No tables selected", onCheck: function (ids) { return setRightChecked(ids); }, renderCellContent: renderCellContent, renderInnerCellContent: renderInnerCellContent, rowCheckIsDisabled: isCheckboxDisabled, hideCheckbox: true, isFirstRowOpenInitially: true }))),
        !isSingleRow && (React.createElement(Stack, { direction: "row", spacing: 1, height: "100%", overflow: "hidden", padding: "20px 32px" },
            React.createElement(Stack, { direction: "column", spacing: 1, overflow: "hidden", width: "100%" },
                React.createElement(Typography, { fontSize: "large" }, "Available"),
                React.createElement(DataCatalogSyncTable, { data: left, columns: columns, innerColumns: innerColumns, filterConfigs: FILTER_CONFIGS, childrenAttribute: CHILDREN_ATTRIBUTE, noDataNotification: "No tables available", onCheck: function (ids) { return setLeftChecked(ids); }, renderCellContent: renderCellContent, renderInnerCellContent: renderInnerCellContent }),
                React.createElement(Footer, { left: leftChecked.length + "/" + left.length + " selected" })),
            React.createElement(Stack, { justifyContent: "center", alignItems: "center" },
                React.createElement(Stack, null,
                    React.createElement(AccurityIconButton, { onClick: moveRight, iconName: "mui:ArrowForwardIos", disabled: !leftChecked.length, color: "primary" }),
                    React.createElement(AccurityIconButton, { onClick: moveLeft, iconName: "mui:ArrowBackIosNew", disabled: !rightChecked.length, color: "primary" }))),
            React.createElement(Stack, { direction: "column", spacing: 1, overflow: "hidden", width: "100%" },
                React.createElement(Typography, { fontSize: "large" }, "Selected"),
                React.createElement(DataCatalogSyncTable, { data: right, columns: columns, innerColumns: innerColumns, filterConfigs: FILTER_CONFIGS, childrenAttribute: CHILDREN_ATTRIBUTE, noDataNotification: "No tables selected", onCheck: function (ids) { return setRightChecked(ids); }, renderCellContent: renderCellContent, renderInnerCellContent: renderInnerCellContent, rowCheckIsDisabled: isCheckboxDisabled }),
                React.createElement(Footer, { left: rightChecked.length + "/" + right.length + " selected", right: (maximumObjects !== undefined) ? sourceObjectsLength + existingObjects + "/" + maximumObjects + " in Accurity" : undefined, rightColor: isMaximumObjectsReached ? 'error' : undefined })))),
        React.createElement(DialogActions, null,
            React.createElement(AccurityButton, { onClick: function () { return handleClose([]); }, color: "secondary" }, isAllSource ? 'SKIP' : 'CANCEL'),
            React.createElement(AccurityButton, { onClick: function () { return handleClose(right); }, disabled: !right.length || isMaximumObjectsReached }, "CONFIRM"))));
}
//# sourceMappingURL=DataStructureSyncSelectionDialog.js.map